import { Button } from '@design/components/button/Button';
import semanticColorStyles from '@design/styles/SemanticColor.module.scss';
import { FollowThemeButton } from '@terminal/components/follow-theme-button/FollowThemeButton';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import type {
  CatalystMarketReactionChartHeaderVariant,
  MarketReactionSubject,
} from '@terminal/modules/market-reaction-charts/types';
import type { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatProportionChange } from '../utils';
import styles from './MarketReactionLegendRow.module.scss';

export interface MarketReactionLegendRowProps {
  icon: ReactNode;
  color: string;
  name?: string;
  ticker?: string;
  proportionChange?: number;
  dateLabel?: string | null;
  chartHeaderVariant?: CatalystMarketReactionChartHeaderVariant;
  subject: MarketReactionSubject;
  trackingSource?: PosthogTrackingSource;
}

export const MarketReactionLegendRow = ({
  icon,
  color,
  name,
  ticker,
  proportionChange,
  dateLabel,
  chartHeaderVariant = 'default',
  subject,
  trackingSource,
}: MarketReactionLegendRowProps) => {
  const { t } = useTranslation('common');
  const { trackWatchlistCreate } = usePosthogEvents();

  const handleWatchlistCreated = (watchlistId: string) => {
    if (trackingSource) {
      trackWatchlistCreate(trackingSource, watchlistId);
    }
  };

  const isThematicMarketCatalyst = chartHeaderVariant === 'marketCatalyst';
  const identityAction =
    isThematicMarketCatalyst && subject.type === 'theme' ? (
      <div data-thematic-market-catalyst-theme-follow-button>
        <FollowThemeButton
          buttonTestId={`thematic-market-catalyst-chart-follow-${subject.data.id}`}
          className={styles.identityActionButton}
          themeId={subject.data.id}
          trackingSource={trackingSource}
          variant="icon"
        />
      </div>
    ) : isThematicMarketCatalyst && subject.type === 'entity' ? (
      <WatchlistTrigger
        onWatchlistCreated={trackingSource ? handleWatchlistCreated : undefined}
        tag={subject.data.tag}
        dropdownProps={{
          strategy: 'fixed',
          placement: 'top-start',
          inPortal: false,
        }}
        renderTrigger={isInWatchlist => (
          <Button
            aria-label={
              isInWatchlist
                ? t('components.watchlistBtn.watching')
                : t('components.watchlistBtn.addToWatchlistLabel')
            }
            className={clsx(
              styles.watchlistButton,
              styles.identityActionButton,
              {
                [styles.watchlistButtonActive]: isInWatchlist,
              }
            )}
            data-testid={`thematic-market-catalyst-chart-watchlist-${subject.data.tag}`}
            data-market-catalyst-watchlist-button
            iconName={isInWatchlist ? 'Checkmark' : 'Add'}
            size="xsmall"
            variant="tertiary"
          />
        )}
      />
    ) : null;
  const proportionChangeNode =
    proportionChange !== undefined ? (
      <span
        className={clsx(styles.proportionChange, {
          [semanticColorStyles.directionPositive]: proportionChange > 0,
          [semanticColorStyles.directionNegative]: proportionChange < 0,
          [semanticColorStyles.directionNeutralSoft]: proportionChange === 0,
        })}
      >
        {formatProportionChange(proportionChange)}
      </span>
    ) : null;

  return (
    <div
      className={clsx(styles.legendRow, {
        [styles.marketCatalyst]: isThematicMarketCatalyst,
      })}
    >
      <div className={styles.legendItem}>
        <div className={styles.iconWrapper}>
          <div
            className={styles.legendItemVector}
            style={{ borderColor: color }}
          />
          {icon}
        </div>
        <div className={styles.entityDetails}>
          <TooltipContainer label={name ?? ''}>
            <span className={styles.entityName}>{name}</span>
          </TooltipContainer>
          {ticker && <span className={styles.entityTag}>{ticker}</span>}
        </div>
        {identityAction && (
          <div className={styles.action}>{identityAction}</div>
        )}
      </div>
      {isThematicMarketCatalyst ? (
        <div className={styles.valueWrapper}>
          {proportionChangeNode}
          {dateLabel && <span className={styles.dateLabel}>{dateLabel}</span>}
        </div>
      ) : (
        proportionChangeNode
      )}
    </div>
  );
};
